import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchMenuCategories,
  addMenuCategory,
  deleteMenuCategory,
  attachMenuToCategory,
  detachMenuFromCategory,
  updateMenuCategorySortIndex,
  reorderMenuCategories,
} from './menuCategoryApi';
import type { MenuItem } from '../Menu/menuSlice';

// MenuCategory の型:
export interface MenuCategory {
  id: string;
  name: string;
  sortIndex: number;
  items: MenuItem[]; 
  // 他の必要なフィールドを追加
}

interface MenuCategoryState {
  categories: MenuCategory[];
  loading: boolean;
  error: string | null;
}

const initialState: MenuCategoryState = {
  categories: [],
  loading: false,
  error: null,
};

const menuCategorySlice = createSlice({
  name: 'menuCategory',
  initialState,
  reducers: {
    setCategories(state, action: PayloadAction<MenuCategory[]>) {
      state.categories = action.payload.filter(category => category && category.id).map(cat => ({
        ...cat,
        items: cat.items || []
      }));
    },
    swapCategoriesLocally(state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) {
      const { fromIndex, toIndex } = action.payload;
      if (fromIndex >= 0 && fromIndex < state.categories.length && 
          toIndex >= 0 && toIndex < state.categories.length) {
        const [movedCategory] = state.categories.splice(fromIndex, 1);
        state.categories.splice(toIndex, 0, movedCategory);
        // sortIndex を更新
        state.categories.forEach((cat, i) => {
          if (cat) {
            cat.sortIndex = i + 1;
          }
        });
      }
    },
    /**
     * ローカルでカテゴリの並び替えを更新
     */
    reorderCategoriesLocally: (state, action: PayloadAction<{ fromIndex: number; toIndex: number }>) => {
      const { fromIndex, toIndex } = action.payload;
      if (fromIndex >= 0 && fromIndex < state.categories.length && 
          toIndex >= 0 && toIndex < state.categories.length) {
        const movedCategory = state.categories.splice(fromIndex, 1)[0];
        state.categories.splice(toIndex, 0, movedCategory);
        // sortIndex を更新
        state.categories.forEach((category, index) => {
          if (category) {
            category.sortIndex = index + 1;
          }
        });
      }
    },
    /**
     * 並び替え後のカテゴリリストを一括設定
     */
    setCategoriesOrder: (state, action: PayloadAction<MenuCategory[]>) => {
      state.categories = action.payload.filter(category => category && category.id).map(cat => ({
        ...cat,
        items: cat.items || []
      }));
    },
  },
  extraReducers: (builder) => {
    // fetchMenuCategories
    builder
      .addCase(fetchMenuCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMenuCategories.fulfilled, (state, action: PayloadAction<MenuCategory[]>) => {
        state.categories = action.payload.filter(category => category && category.id).map(cat => ({
          ...cat,
          items: cat.items || []
        }));
        state.loading = false;
      })
      .addCase(fetchMenuCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // addMenuCategory
    builder.addCase(addMenuCategory.fulfilled, (state, action: PayloadAction<MenuCategory>) => {
      if (action.payload && action.payload.id) {
        const newCategory = {
          ...action.payload,
          items: [],
          sortIndex: state.categories.length + 1
        };
        state.categories.push(newCategory);
      }
    });

    // deleteMenuCategory
    builder.addCase(deleteMenuCategory.fulfilled, (state, action) => {
      const { categoryId } = action.meta.arg;
      state.categories = state.categories.filter((cat) => cat && cat.id !== categoryId);
    });

    // attachMenuToCategory
    builder.addCase(attachMenuToCategory.fulfilled, (state, action: PayloadAction<MenuCategory>) => {
      const updatedCat = action.payload;
      if (updatedCat && updatedCat.id) {
        const index = state.categories.findIndex((cat) => cat && cat.id === updatedCat.id);
        if (index >= 0) {
          state.categories[index] = {
            ...updatedCat,
            items: updatedCat.items || []
          };
        }
      }
    });

    // detachMenuFromCategory
    builder.addCase(detachMenuFromCategory.fulfilled, (state, action) => {
      const { menuCategoryId, menuId } = action.meta.arg;
      const categoryIndex = state.categories.findIndex((cat) => cat && cat.id === menuCategoryId);
      if (categoryIndex >= 0) {
        const category = state.categories[categoryIndex];
        category.items = category.items.filter(item => item && item.id !== menuId);
      }
    });

    // updateMenuCategorySortIndex
    builder.addCase(updateMenuCategorySortIndex.fulfilled, (state, action) => {
      // ソート順更新後の処理は特に不要
    });

    // reorderMenuCategories
    builder.addCase(reorderMenuCategories.fulfilled, (state, action) => {
      // 並び替え完了後、最新の状態を反映
      // 現在のstate.categoriesの並び順が正しいので、そのまま保持する
      // 正しい並び順を維持するために・現在の状態を使用
    });
  },
});

export const { setCategories, swapCategoriesLocally, reorderCategoriesLocally, setCategoriesOrder } = menuCategorySlice.actions;

export default menuCategorySlice.reducer;